import { useColorMode } from '@chakra-ui/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CaretDownIcon } from '@radix-ui/react-icons';
import React, { ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import { colors, keyframes, styled } from '../Stitches';
import { routes } from '../data/routes';
import Emoji from './Emoji';

const NavDropDown = ({ onLogOut }: { onLogOut: () => void }) => {
  const { colorMode } = useColorMode();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton aria-label="Open app menu" theme={colorMode}>
          <CaretDownIcon />
        </IconButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenuContent sideOffset={5} theme={colorMode}>
          <Link to={routes.invitations}>
            <ThemedDropdownMenuItem>
              <LeftSlot>
                <Emoji symbol="📮" label="Emoji of a post box" />
              </LeftSlot>
              View Invitations
            </ThemedDropdownMenuItem>
          </Link>
          <Link to={routes.accountSettings}>
            <ThemedDropdownMenuItem>
              <LeftSlot>
                <Emoji symbol="⚙️" label="Emoji of a cog" />
              </LeftSlot>
              Account Settings
            </ThemedDropdownMenuItem>
          </Link>
          <DropdownMenuSeparator theme={colorMode} />

          <Link to={routes.helpAndFeedback}>
            <ThemedDropdownMenuItem>
              <LeftSlot>
                <Emoji symbol="🙋‍♀️" label="Emoji of a woman waving" />
              </LeftSlot>
              Help and Feedback
            </ThemedDropdownMenuItem>
          </Link>
          <a href={routes.site.blog}>
            <ThemedDropdownMenuItem>
              <LeftSlot>
                <Emoji symbol="️📚" label="Emoji of a stack of books" />
              </LeftSlot>
              LetterDesk Blog
            </ThemedDropdownMenuItem>
          </a>
          <a href={routes.site.roots} rel="noreferrer" target="_blank">
            <ThemedDropdownMenuItem>
              <LeftSlot>
                <Emoji symbol="️🌱" label="Emoji of a sapling" />
              </LeftSlot>
              Upcoming features
            </ThemedDropdownMenuItem>
          </a>
          <DropdownMenuSeparator theme={colorMode} />
          <Link to={routes.root}>
            <ThemedDropdownMenuItem onClick={onLogOut}>
              <LeftSlot>
                <Emoji symbol="✌️" label="Emoji of a handing making the peace sign" />
              </LeftSlot>
              Sign Out
            </ThemedDropdownMenuItem>
          </Link>

          <DropdownMenuArrow theme={colorMode} />
        </DropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const contentStyles = {
  padding: '$4',

  borderRadius: '$4',
  marginRight: '$5',

  // TODO: Replace this shadow with something from the design system
  boxShadow:
    '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',

  animationDuration: '400ms',
  animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',

  willChange: 'transform, opacity',

  '&[data-state="open"]': {
    '&[data-side="top"]': { animationName: slideDownAndFade },
    '&[data-side="right"]': { animationName: slideLeftAndFade },
    '&[data-side="bottom"]': { animationName: slideUpAndFade },
    '&[data-side="left"]': { animationName: slideRightAndFade },
  },

  backdropFilter: 'blur(16px)',

  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: colors.dark.whiteAlpha400,

  variants: {
    theme: {
      light: {
        borderColor: colors.gray500,
        backgroundColor: colors.gray25,
      },
      dark: {
        borderColor: colors.dark.whiteAlpha400,
      },
    },
  },
};

const DropdownMenuContent = styled(DropdownMenu.Content, contentStyles);

const DropdownMenuArrow = styled(DropdownMenu.Arrow, {
  marginRight: '$5',
  variants: {
    theme: {
      light: { fill: colors.gray500 },
      dark: { fill: colors.dark.whiteAlpha400 },
    },
  },
});

const itemStyles = {
  all: 'unset',
  color: 'black',
  borderRadius: 3,
  display: 'flex',
  alignItems: 'center',
  paddingInline: '$4',
  paddingBlock: '$3',
  userSelect: 'none',
  position: 'relative',

  fontFamily: '$mono',
  fontSize: '$2',
  lineHeight: 1.1,

  transition: 'all 48ms ease-out',

  '&[data-highlighted]': {
    backgroundColor: colors.ldPurple,
    color: 'white',
    fontWeight: 700,
  },

  variants: {
    theme: {
      light: { color: 'black' },
      dark: { color: 'white' },
    },
  },
};

const DropdownMenuItem = styled(DropdownMenu.Item, itemStyles);

const ThemedDropdownMenuItem = ({
  children,
  ...props
}: { children: React.ReactNode } & ComponentProps<typeof DropdownMenuItem>) => {
  const { colorMode } = useColorMode();
  return (
    <DropdownMenuItem theme={colorMode} {...props}>
      {children}
    </DropdownMenuItem>
  );
};

const DropdownMenuSeparator = styled(DropdownMenu.Separator, {
  height: 1,
  margin: 5,

  variants: {
    theme: {
      light: {
        backgroundColor: colors.ldPurple,
      },
      dark: {
        backgroundColor: colors.dark.ldPurple,
      },
    },
  },
});

const LeftSlot = styled('div', {
  marginRight: 'auto',
  paddingRight: '$5',
});

const cursor = { cursor: 'pointer' };

const IconButton = styled('button', {
  all: 'unset',
  fontFamily: 'inherit',
  borderRadius: '$3',
  height: 35,
  width: 35,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',

  borderWidth: 1,
  borderStyle: 'solid',

  transition: 'all 100ms ease-out',

  '&hover': { cursor: 'pointer' },

  variants: {
    theme: {
      light: {
        backgroundColor: 'white',
        '&:hover, &[data-state=open]': { ...cursor, backgroundColor: colors.gray50 },
        '&:focus': { boxShadow: `0 0 0 1px ${colors.gray300}` },
        borderColor: colors.gray200,
      },
      dark: {
        color: 'white',
        backgroundColor: '',
        '&:hover, &[data-state=open]': { ...cursor, backgroundColor: colors.dark.whiteAlpha200 },
        '&:focus': { boxShadow: `0 0 0 0.5px ${colors.dark.whiteAlpha400}` },
        borderColor: colors.dark.whiteAlpha100,
      },
    },
  },
});

export default NavDropDown;
